import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Ban, Trash2 } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Chatter } from '../../componentes/chatter';
import { Carregando, Erro } from '../../componentes/estados';
import { AreaSensivel, linhasExtras, useCamposPersonalizados } from '../../componentes/formulario';
import { anunciarOcultos } from '../../componentes/sensivel';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { del, get, mensagemDeErro, post } from '../../nucleo/api';
import { data, dataHora, dinheiro, numero, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { Linha, Secao } from './comum';
import { COR_COMPRA, COR_CONTA, type CompraDetalhe } from './compra-tipos';
import { invalidarCompras, qtd } from './tipos';
import { LinkParceiro } from '../../componentes/link-parceiro';
import { rotuloNomeForma } from '../../nucleo/forma-pagamento';

/** Condição gravada (avista, 30/60, mensal:3) no idioma do app. */
function useCondicao() {
  const { t } = useTranslation('compras');
  return (c: CompraDetalhe) => {
    if (c.condicao_pagamento === 'avista') return t('compra.condicoes.avista');
    if (c.condicao_pagamento.startsWith('mensal:')) return `${t('compra.condicoes.mensal')} (${c.parcelas})`;
    return `${t('compra.condicoes.prazos')} (${c.condicao_pagamento})`;
  };
}

/** Fase 2 (W8): ficha da compra direta com as contas a pagar geradas (estado de agora no Contas) e o cancelamento. */
export function DetalheCompra({ id, aoFechar }: { id: string | null; aoFechar: () => void }) {
  const { t } = useTranslation('compras');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const condicao = useCondicao();
  const defsExtras = useCamposPersonalizados('compras.compra');
  const q = useQuery({ queryKey: ['/compras/compras', 'obter', id], queryFn: () => get<CompraDetalhe>(`/compras/compras/${id}`), enabled: !!id });
  const d = q.data;
  const [cancelando, setCancelando] = useState(false);
  const [motivo, setMotivo] = useState('');

  const cancelar = useMutation({
    mutationFn: () => post<{ contas_canceladas: number; contas_com_baixa: number }>(`/compras/compras/${id}/cancelar`, { motivo: motivo.trim() || undefined }),
    onSuccess: async (r) => {
      await invalidarCompras(qc);
      setCancelando(false);
      setMotivo('');
      toast.sucesso(t('compra.cancelada', { canceladas: r?.contas_canceladas ?? 0, mantidas: r?.contas_com_baixa ?? 0 }));
    },
    onError: (e) => {
      setCancelando(false);
      toast.erro(mensagemDeErro(e));
    },
  });

  // Fase 2 (W16): a compra cancelada vai para a lixeira (30 dias); conta viva no Contas recusa com 409 e a lista do que segura.
  const [excluindo, setExcluindo] = useState(false);
  const excluir = useMutation({
    mutationFn: () => del(`/compras/compras/${id}`),
    onSuccess: async () => {
      setExcluindo(false);
      toast.sucesso(t('compra.excluida'));
      // A ficha (e o histórico dela) some antes de recarregar as listas: senão ela mesma pede a compra excluída de novo (404).
      qc.removeQueries({ predicate: (q) => !!id && JSON.stringify(q.queryKey).includes(id) });
      aoFechar();
      await invalidarCompras(qc);
    },
    onError: (e) => {
      setExcluindo(false);
      toast.erro(mensagemDeErro(e));
    },
  });

  const linkContas = d && s.instalado('contas') && s.pode('contas.ver') ? `/financeiro/contas/a-pagar?parceiro_id=${d.fornecedor_id}&estado=todos` : null;

  return (
    <>
      <Drawer
        aberto={!!id}
        aoFechar={aoFechar}
        largura="lg"
        titulo={d ? t('compra.titulo', { numero: d.numero }) : t('compra.tituloCurto')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={aoFechar}>
              {tn('comum.fechar')}
            </Botao>
            {d?.estado === 'cancelada' && s.pode('compras.excluir') && (
              <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setExcluindo(true)}>
                {t('compra.excluir')}
              </Botao>
            )}
            {d?.estado === 'ativa' && s.pode('compras.cancelar') && (
              <Botao variante="perigo" icone={<Ban className="size-4" />} onClick={() => setCancelando(true)}>
                {t('compra.cancelar')}
              </Botao>
            )}
          </>
        }
      >
        {q.isPending && !!id && <Carregando />}
        {q.isError && <Erro erro={q.error} aoTentar={() => q.refetch()} />}
        {d && (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Selo cor={COR_COMPRA[d.estado]}>{t(`compra.estado.${d.estado}`)}</Selo>
            </div>
            <p className="numero mb-1 text-3xl font-semibold text-tinta-1">{anunciarOcultos(dinheiro(d.total_centavos, d.moeda))}</p>
            <p className="mb-4 text-sm text-tinta-3">{t('compra.total')}</p>

            <dl className="divide-y divide-borda">
              {/* Lote 09/10 (C3): tipo da compra e conta bancária prevista. */}
              {d.tipo && <Linha rotulo={t('c3.tipo')}>{t(`c3.tipos.${d.tipo}`)}</Linha>}
              <Linha rotulo={t('compra.fornecedor')}>
                <LinkParceiro id={d.fornecedor_id} nome={d.fornecedor_nome} />
              </Linha>
              <Linha rotulo={t('compra.descricao')}>{textoLivre(d.descricao, d.fornecedor_nome)}</Linha>
              <Linha rotulo={t('compra.competencia')}>{data(d.competencia)}</Linha>
              <Linha rotulo={t('compra.nf')}>{[d.nf_numero, d.nf_serie && `${t('compra.nfSerie')} ${d.nf_serie}`, d.nf_emissao && data(d.nf_emissao)].filter(Boolean).join(' · ')}</Linha>
              {d.nf_chave && (
                <Linha rotulo={t('compra.nfChave')}>
                  <span className="font-mono text-xs">{d.nf_chave}</span>
                </Linha>
              )}
              <Linha rotulo={t('compra.condicao')}>{condicao(d)}</Linha>
              <Linha rotulo={t('compra.primeiroVencimento')}>{data(d.primeiro_vencimento)}</Linha>
              <Linha rotulo={t('compra.forma')}>{d.forma_pagamento_nome ? rotuloNomeForma(d.forma_pagamento_nome) : (d.forma_pagamento ? t(`compra.formas.${d.forma_pagamento}`) : '')}</Linha>
              {(d.conta_prevista_nome || !d.conta_prevista_id) && <Linha rotulo={t('c3.contaBancaria')}>{d.conta_prevista_nome ?? ''}</Linha>}
              {/* Review r28 spec (B2): sem `financeiro.ver` a API manda o id com o nome nulo; a linha some (como no detalhe do
                  documento), em vez de "Não informado" numa compra que tem categoria e centro. Sem id, segue "Não informado". */}
              {d.itens === 0 && (
                <>
                  {(d.categoria_nome || !d.categoria_id) && <Linha rotulo={t('compra.categoria')}>{d.categoria_nome ?? ''}</Linha>}
                  {(d.centro_custo_nome || !d.centro_custo_id) && <Linha rotulo={t('compra.centroCusto')}>{d.centro_custo_nome ?? ''}</Linha>}
                </>
              )}
              {d.cancelado_em && <Linha rotulo={t('compra.canceladaEm')}>{dataHora(d.cancelado_em)}</Linha>}
              {d.motivo_cancelamento && <Linha rotulo={t('compra.motivo')}>{textoLivre(d.motivo_cancelamento)}</Linha>}
              {d.observacoes && <Linha rotulo={t('compra.observacoes')}>{textoLivre(d.observacoes)}</Linha>}
              {/* Fase 2 (W19, K5): campos personalizados pelo formatador único (modo discreto respeitado). */}
              {linhasExtras(defsExtras, d.extras, d.fornecedor_nome).map((l) => (
                <Linha key={l.chave} rotulo={l.rotulo}>
                  {l.valor}
                </Linha>
              ))}
            </dl>

            <Secao titulo={t('compra.itens')}>
              {d.itens_detalhe.length === 0 ? (
                <p className="text-sm text-tinta-3">{t('compra.semItens')}</p>
              ) : (
                <ul className="divide-y divide-borda rounded-lg border border-borda">
                  {d.itens_detalhe.map((i) => (
                    <li key={i.id} className="flex min-w-0 items-start justify-between gap-3 px-3 py-2 text-sm">
                      <span className="min-w-0">
                        <TextoCortado className="block">{textoLivre(i.descricao)}</TextoCortado>
                        <TextoCortado className="numero block text-xs text-tinta-3">
                          {qtd(i.quantidade, numero)} x {anunciarOcultos(dinheiro(i.valor_unitario_centavos, d.moeda))}
                          {[i.categoria_nome, i.centro_custo_nome].filter(Boolean).length ? ` · ${[i.categoria_nome, i.centro_custo_nome].filter(Boolean).join(' · ')}` : ''}
                        </TextoCortado>
                      </span>
                      <span className="numero shrink-0">{anunciarOcultos(dinheiro(i.total_centavos, d.moeda))}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Secao>

            <Secao titulo={t('compra.contas')} acao={linkContas ? <Link to={linkContas} className="text-sm text-acento-forte hover:underline">{t('compra.verNoContas')}</Link> : undefined}>
              <ul className="divide-y divide-borda rounded-lg border border-borda">
                {d.contas.map((c) => (
                  <li key={c.id} className="flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-2 text-sm">
                    <span className="flex min-w-0 flex-col">
                      <span className="font-mono text-xs">{c.numero ?? ''}</span>
                      <span className="text-xs text-tinta-3">
                        {c.parcelas > 1 ? `${t('compra.parcela', { parcela: c.parcela, parcelas: c.parcelas })} · ` : ''}
                        {t('compra.venceEm', { data: data(c.vencimento) })}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      {c.estado && <Selo cor={COR_CONTA[c.estado] ?? 'neutro'}>{t(`compra.estadoConta.${c.estado}`, { defaultValue: c.estado })}</Selo>}
                      <span className="numero">{anunciarOcultos(dinheiro(c.valor_centavos, d.moeda))}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </Secao>

            <div className="mt-6 border-t border-borda pt-4">
              <Chatter entidade="compras.compra" registroId={d.id} titulo={t('compra.titulo', { numero: d.numero })} rotulosCampos={{ estado: t('campos.estado') }} />
            </div>
          </>
        )}
      </Drawer>
      <Confirmar aberto={excluindo} aoFechar={() => setExcluindo(false)} aoConfirmar={() => excluir.mutate()} carregando={excluir.isPending} titulo={t('compra.excluirTitulo')} texto={t('compra.excluirTexto')} perigoso />
      <Modal
        aberto={cancelando}
        aoFechar={() => setCancelando(false)}
        titulo={t('compra.cancelarTitulo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setCancelando(false)}>
              {tn('comum.voltar')}
            </Botao>
            <Botao variante="perigo" carregando={cancelar.isPending} onClick={() => cancelar.mutate()}>
              {t('compra.cancelar')}
            </Botao>
          </>
        }
      >
        <p className="mb-3 text-sm text-tinta-2">{t('compra.cancelarTexto')}</p>
        <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
          {t('compra.motivo')}
          <AreaSensivel rows={3} maxLength={300} value={motivo} onChange={(e) => setMotivo(e.target.value)} className="w-full rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco" />
        </label>
      </Modal>
    </>
  );
}
